import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { History, Play, X, MessageSquare } from "lucide-react";
import api, { reportApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const timeAgo = (iso) => {
  const mins = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

export function useActiveSessions() {
  const [items, setItems] = useState([]);
  const refresh = () => api.get("/roleplay/active").then((r) => setItems(r.data)).catch(reportApiError("ResumeSessions"));
  useEffect(() => { refresh(); }, []);
  const discard = async (id) => {
    setItems((s) => s.filter((x) => x.id !== id));
    try { await api.post(`/roleplay/${id}/abandon`); } catch { toast.error("Couldn't discard that conversation"); refresh(); }
  };
  return { items, discard, refresh };
}

function ResumeRow({ s, onResume, onDiscard }) {
  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98 }}
      className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-slate-200/80 hover:border-emerald-300 transition-colors" data-testid={`resume-session-${s.id}`}>
      <img src={s.persona_avatar} alt="" className="w-11 h-11 rounded-full object-cover object-top ring-2 ring-white shadow shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900 text-sm">{s.persona_name}</span>
          <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">{s.channel_label}</span>
          <span className="text-[11px] text-slate-400">{timeAgo(s.last_at)}</span>
        </div>
        <div className="text-xs text-slate-500 truncate mt-0.5 flex items-center gap-1"><MessageSquare className="w-3 h-3 shrink-0" /> {s.user_turns} {s.user_turns === 1 ? "reply" : "replies"} · “{s.last_message}”</div>
      </div>
      <Button onClick={() => onResume(s)} size="sm" className="h-9 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 shrink-0" data-testid={`resume-btn-${s.id}`}>
        <Play className="w-3.5 h-3.5 mr-1.5" /> Resume
      </Button>
      <button onClick={() => onDiscard(s.id)} title="Discard this conversation" className="p-2 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0" data-testid={`discard-btn-${s.id}`}>
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
}

export function ResumeNudge() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const COOLDOWN = 10 * 60 * 1000;
    let hiddenAt = null;
    const nudge = async () => {
      const last = Number(sessionStorage.getItem("pc_resume_nudged_at") || 0);
      if (Date.now() - last < COOLDOWN) return;
      let items = [];
      try { items = (await api.get("/roleplay/active")).data; } catch { return; }
      const s = items[0];
      if (!s) return;
      const here = new URLSearchParams(window.location.search).get("session");
      if (window.location.pathname === "/app/roleplay" && here === s.id) return;
      sessionStorage.setItem("pc_resume_nudged_at", String(Date.now()));
      const more = items.length > 1 ? ` (+${items.length - 1} more waiting)` : "";
      toast(`Continue with ${s.persona_name.split(" ")[0]}?`, {
        id: "resume-nudge", duration: 9000, icon: <History className="w-4 h-4 text-emerald-600" />,
        description: `You left this ${s.channel_label.toLowerCase()} conversation ${timeAgo(s.last_at)}${more}.`,
        action: { label: "Continue", onClick: () => navigate(`/app/roleplay?session=${s.id}`) },
      });
    };
    nudge();
    const onVis = () => {
      if (document.visibilityState === "hidden") hiddenAt = Date.now();
      else if (hiddenAt && Date.now() - hiddenAt > 2 * 60 * 1000) nudge();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []); // eslint-disable-line
  useEffect(() => { if (location.pathname === "/app/roleplay" && location.search.includes("session=")) toast.dismiss("resume-nudge"); }, [location]);
  return null;
}

export function ResumeSessions({ onResume, className = "", max = 5 }) {
  const { items: all, discard } = useActiveSessions();
  const items = all.slice(0, max);
  if (!items.length) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-cyan-50 p-4 sm:p-5 shadow-[0_4px_20px_-4px_rgba(16,185,129,0.15)] ${className}`} data-testid="resume-sessions">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-700 flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Pick up where you left off</p>
          <p className="text-sm text-slate-600 mt-0.5">Your conversations are saved automatically — jump straight back in.</p>
        </div>
        <span className="text-xs font-semibold text-slate-500 bg-white border border-slate-200 rounded-full px-2.5 py-1 shrink-0" data-testid="resume-count">{all.length} in progress</span>
      </div>
      <div className="space-y-2">
        <AnimatePresence initial={false}>
          {items.map((s) => <ResumeRow key={s.id} s={s} onResume={onResume} onDiscard={discard} />)}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
